import axios from "axios";
import { sanitizeErrorMessage } from "../utils/sanitizeErrorMessage";

const _normalizeApiBase = (raw) => {
  const cleaned = String(raw || "").trim().replace(/\/+$/, "");
  if (!cleaned) return "";
  return cleaned.endsWith("/api") ? cleaned : `${cleaned}/api`;
};

const _resolveBaseUrl = () => {
  const envBase = _normalizeApiBase(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL);
  if (envBase) return envBase;

  const hostname =
    typeof window !== "undefined" && window.location ? window.location.hostname : "";
  const protocol =
    typeof window !== "undefined" && window.location ? window.location.protocol : "";

  // Production domain mapping: frontend -> backend subdomain (HTTPS only).
  if (protocol === "https:" && hostname === "enterpriseaihub.thbscoetg.com") {
    return "https://backend.enterpriseaihub.thbscoetg.com/api";
  }

  // Local frontend defaults to local backend.
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8002/api";
  }

  // Same host as the UI (e.g. http://<public-ip>:3000 -> http://<public-ip>:8002/api).
  if (hostname) {
    const scheme = protocol || "http:";
    return `${scheme}//${hostname}:8002/api`;
  }

  return "http://54.163.239.237:8002/api";
};

let baseUrl = _resolveBaseUrl();

/** Current resolved API base (for admin tools like starter kit download). */
export const getApiBaseUrl = () => baseUrl;

let authFailureHandler = null;
let isHandlingAuthFailure = false;
const _rbacProjectsInflight = new Map();
const _rbacProjectsCache = new Map();
const RBAC_PROJECTS_CACHE_TTL_MS = 8000;
const _starterEntitlementsInflight = new Map();
const _starterEntitlementsCache = new Map();
const STARTER_ENTITLEMENTS_CACHE_TTL_MS = 15000;

export const setAuthFailureHandler = (handler) => {
  authFailureHandler = typeof handler === "function" ? handler : null;
};

const sanitizeApiErrorPayload = (error) => {
  const data = error?.response?.data;
  if (data && data.detail != null) {
    data.detail = sanitizeErrorMessage(data.detail);
  }
  if (error?.message) {
    error.message = sanitizeErrorMessage(error.message);
  }
  return error;
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    sanitizeApiErrorPayload(error);
    const status = error?.response?.status;
    const detail = String(error?.response?.data?.detail || "").toLowerCase();
    const authHeader =
      error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
    const bearerValue =
      typeof authHeader === "string" && /^bearer\s+/i.test(authHeader)
        ? authHeader.replace(/^bearer\s+/i, "").trim()
        : "";
    const hasBearerToken =
      bearerValue.length > 0 && bearerValue !== "undefined" && bearerValue !== "null";
    const isAuthError =
      status === 401 || detail.includes("invalid token") || detail.includes("token");

    if (!isHandlingAuthFailure && hasBearerToken && isAuthError && authFailureHandler) {
      isHandlingAuthFailure = true;
      try {
        authFailureHandler();
      } finally {
        // allow future auth failures after current logout cycle
        setTimeout(() => {
          isHandlingAuthFailure = false;
        }, 0);
      }
    }

    return Promise.reject(error);
  }
);


export const login = async (username, password) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/auth/login`,
    data: { username, password },
  });

export const getCurrentUser = async (token) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/auth/me`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const registerUser = async (username, password) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/auth/register`,
    data: { username, password },
  });

export const healthCheck = async () =>
  await axios({
    method: `get`,
    url: `${baseUrl}/health`,
  });

export const dependencyHealthCheck = async (token, options = {}) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/health/dependencies`,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    signal: options.signal,
  });

export const root = async () =>
  await axios({
    method: `get`,
    url: `${baseUrl}/`,
  });

export const createProject = async (token, name, description, config = null) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/`,
    headers: { Authorization: `Bearer ${token}` },
    data: config ? { name, description, config } : { name, description },
  });

export const listProjects = async () =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/`,
  });

export const getProject = async (projectId, options = {}) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}`,
    signal: options.signal,
    headers: options.token ? { Authorization: `Bearer ${options.token}` } : undefined,
  });

export const deleteProject = async (token, projectId, emptyS3 = false) =>
  await axios({
    method: `delete`,
    url: `${baseUrl}/projects/${projectId}${emptyS3 ? '?empty_s3=true' : ''}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const uploadDocument = async (token, projectId, file) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/documents/upload`,
    headers: { Authorization: `Bearer ${token}` },
    data: file,
  });

export const listDocuments = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/documents`,
  });

export const getDocument = async (projectId, docId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/documents/${docId}`,
  });

export const deleteDocument = async (token, projectId, docId) =>
  await axios({
    method: `delete`,
    url: `${baseUrl}/projects/${projectId}/documents/${docId}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const generateChunks = async (token, projectId, docId, strategy = null) => {
  const data = strategy ? { strategy } : {};
  return await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/documents/${docId}/chunk`,
    headers: { Authorization: `Bearer ${token}` },
    data: data,
  });
};

export const getAIRecommendations = async (token, projectId, docId) => {
  try {
    const response = await axios({
      method: `get`,
      url: `${baseUrl}/projects/${projectId}/documents/${docId}/recommend-strategies`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    console.error('getAIRecommendations API error:', error);
    throw error;
  }
};

export const compareChunkingStrategies = async (token, projectId, docId, strategies = null) => {
  try {
    const response = await axios({
      method: `post`,
      url: `${baseUrl}/projects/${projectId}/documents/${docId}/compare-strategies`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      data: strategies ? { strategies } : {},
      timeout: 300000, // 5 minutes timeout for long-running comparison
    });
    return response;
  } catch (error) {
    console.error('compareChunkingStrategies API error:', error);
    console.error('Request URL:', `${baseUrl}/projects/${projectId}/documents/${docId}/compare-strategies`);
    console.error('Error response:', error.response);
    throw error;
  }
};

export const getChunks = async (projectId, docId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/documents/${docId}/chunks`,
  });

export const generateEmbeddings = async (token, projectId, docId = null) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/embeddings/run`,
    headers: { Authorization: `Bearer ${token}` },
    data: { doc_id: docId },
  });

export const getEmbeddingStatus = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/embeddings/status`,
  });

export const pushVectors = async (token, projectId, docId = null) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/vectors/push`,
    headers: { Authorization: `Bearer ${token}` },
    data: docId ? { doc_id: docId } : {},
  });

export const getVectorStatus = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/vectors/status`,
  });

export const getVectorDetails = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/vectors/details`,
  });

export const checkVectorHealth = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/vectors/health`,
  });

export const assessLocalVectorCache = async (token, projectId, docId = null) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/vectors/local-cache-assessment`,
    headers: { Authorization: `Bearer ${token}` },
    params: docId ? { doc_id: docId } : {},
  });

export const rebuildVectorsFromCache = async (token, projectId, docId = null) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/vectors/rebuild-from-cache`,
    headers: { Authorization: `Bearer ${token}` },
    data: docId ? { doc_id: docId } : {},
  });

export const semanticSearch = async (projectId, query, applyGuardrails = true) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/search`,
    data: { query, apply_guardrails: applyGuardrails },
  });

export const simpleSearch = async (projectId, query, topK = 5) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/search/simple`,
    data: { query, top_k: topK },
  });

export const getConfig = async (projectId, options = {}) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/config/`,
    headers: options.token ? { Authorization: `Bearer ${options.token}` } : undefined,
  });

export const updateConfig = async (token, projectId, config) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/config/`,
    headers: { Authorization: `Bearer ${token}` },
    data: config,
  });

export const runVectorRetention = async (token, projectId) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/vector-retention/run`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const patchFilePipelineFileRetention = async (token, projectId, fileId, vectorRetention) =>
  await axios({
    method: `patch`,
    url: `${baseUrl}/projects/${projectId}/file-pipeline/files/${encodeURIComponent(fileId)}`,
    headers: { Authorization: `Bearer ${token}` },
    data: { vector_retention: vectorRetention },
  });

export const patchFilePipelineFileRole = async (token, projectId, fileId, roleId) =>
  await axios({
    method: "patch",
    url: `${baseUrl}/projects/${projectId}/file-pipeline/files/${encodeURIComponent(fileId)}`,
    headers: { Authorization: `Bearer ${token}` },
    data: { role_id: roleId },
  });

export const patchFilePipelineFileCategory = patchFilePipelineFileRole;

export const verifyS3Config = async (token, payload) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/s3/validate`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const verifyConfluenceConfig = async (token, payload) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/confluence/validate`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listConfluenceFolders = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/confluence/list-folders`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listConfluenceSpaces = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/confluence/list-spaces`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listConfluenceSpacesForProject = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/list-spaces`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listConfluenceFoldersForProject = async (token, projectId, params = {}) =>
  await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/folders`,
    headers: { Authorization: `Bearer ${token}` },
    params,
  });

export const listConfluencePageTreeForProject = async (token, projectId, params = {}) =>
  await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/page-tree`,
    headers: { Authorization: `Bearer ${token}` },
    params,
  });

export const updateConfluenceSyncScope = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/sync-scope`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const connectAzureWiki = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/azure-wiki/connect`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listAzureWikiProjectsForProject = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/list-projects`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listAzureWikiWikisForProject = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/list-wikis`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listAzureWikiPageTreeForProject = async (token, projectId, params = {}) =>
  await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/page-tree`,
    headers: { Authorization: `Bearer ${token}` },
    params,
  });

export const updateAzureWikiSyncScope = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/sync-scope`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const updateAzureWikiConfig = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/config`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const connectGithub = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/github/connect`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const verifyGithubConfig = connectGithub;

export const listGithubReposForProject = async (token, projectId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/github/list-repos`,
    headers: { Authorization: `Bearer ${token}` },
    data: {},
  });

export const listGithubBranchesForProject = async (token, projectId, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/github/list-branches`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listGithubTreeForProject = async (token, projectId, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/github/list-tree`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const updateGithubSyncScope = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/github/sync-scope`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const updateGithubConfig = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/github/config`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const verifyJiraConfig = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/jira/validate`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listJiraProjects = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/jira/list-projects`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const listJiraProjectsForProject = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/list-projects`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const getJiraIssueScope = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/jira/issue-scope`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const getJiraIssueScopeForProject = async (token, projectId, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/issue-scope`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const updateJiraConfig = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/config`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const getKnowledgeSourceStatus = async (token, projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/knowledge-source/status`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getConfluenceWebhookSetup = async (token, projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/knowledge-source/webhook-setup`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const syncKnowledgeSource = async (token, projectId, payload = {}) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/knowledge-source/sync`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const getJiraConnectorStatus = async (token, projectId) =>
  await axios({
    method: 'get',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/connector-status`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getJiraMonitoredIssues = async (token, projectId) =>
  await axios({
    method: 'get',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/monitored-issues`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const setJiraIssueMonitor = async (token, projectId, payload) =>
  await axios({
    method: 'put',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/monitored-issues`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const checkJiraMonitoredUpdates = async (token, projectId) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/monitored-issues/check`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const applyJiraMonitoredUpdate = async (token, projectId, issueKey, fileId = null) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/monitored-issues/${encodeURIComponent(issueKey)}/apply-update`,
    headers: { Authorization: `Bearer ${token}` },
    params: fileId ? { file_id: fileId } : undefined,
  });

export const dismissJiraMonitoredUpdate = async (token, projectId, issueKey) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/jira/monitored-issues/${encodeURIComponent(issueKey)}/dismiss-update`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getConfluenceMonitoredPages = async (token, projectId) =>
  await axios({
    method: 'get',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/monitored-pages`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const setConfluencePageMonitor = async (token, projectId, payload) =>
  await axios({
    method: 'put',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/monitored-pages`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const checkConfluenceMonitoredUpdates = async (token, projectId) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/monitored-pages/check`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const applyConfluenceMonitoredUpdate = async (token, projectId, pageId, fileId = null) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/monitored-pages/${encodeURIComponent(pageId)}/apply-update`,
    headers: { Authorization: `Bearer ${token}` },
    params: fileId ? { file_id: fileId } : undefined,
  });

export const dismissConfluenceMonitoredUpdate = async (token, projectId, pageId) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/confluence/monitored-pages/${encodeURIComponent(pageId)}/dismiss-update`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAzureWikiMonitoredPages = async (token, projectId) =>
  await axios({
    method: 'get',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/monitored-pages`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const setAzureWikiPageMonitor = async (token, projectId, payload) =>
  await axios({
    method: 'put',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/monitored-pages`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const checkAzureWikiMonitoredUpdates = async (token, projectId) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/monitored-pages/check`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const applyAzureWikiMonitoredUpdate = async (token, projectId, pageId, fileId = null) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/monitored-pages/${encodeURIComponent(pageId)}/apply-update`,
    headers: { Authorization: `Bearer ${token}` },
    params: fileId ? { file_id: fileId } : undefined,
  });

export const dismissAzureWikiMonitoredUpdate = async (token, projectId, pageId) =>
  await axios({
    method: 'post',
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/azure-wiki/monitored-pages/${encodeURIComponent(pageId)}/dismiss-update`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getGuardrails = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/guardrails`,
  });

export const getAvailableGuards = async (projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/guardrails/available-guards`,
  });

export const updateGuardrails = async (token, projectId, guardrails) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/guardrails`,
    headers: { Authorization: `Bearer ${token}` },
    data: guardrails,
  });

export const testGuardrails = async (guardrailConfig, text, filterType = 'input') =>
  await axios({
    method: `post`,
    url: `${baseUrl}/guardrails/test`,
    data: {
      enabled: guardrailConfig.enabled ?? true,
      input_guards: guardrailConfig.input_guards || [],
      output_guards: guardrailConfig.output_guards || [],
      text: text,
      filter_type: filterType,
    },
  });

export const chat = async (
  token,
  projectId,
  message,
  sessionId = null,
  applyGuardrails = true,
  includeSources = true,
  options = {},
) =>
  await axios({
    method: `post`,
    url: `${baseUrl}/projects/${projectId}/chatbot/`,
    headers: { Authorization: `Bearer ${token}` },
    data: {
      message,
      session_id: sessionId,
      apply_guardrails: applyGuardrails,
      include_sources: includeSources,
      deep_agent_enabled: options.deepAgentEnabled,
      reasoning_enabled: options.reasoningEnabled,
      reasoning_depth: options.reasoningDepth,
      chat_mode: options.chatMode,
      chat_role: options.chatRole,
      memory_enabled: options.memoryEnabled,
      direct_llm_fallback_enabled: options.directLlmFallbackEnabled === true,
    },
  });

export const chatStream = async (token, projectId, message, sessionId, options = {}) => {
  const url = `${baseUrl}/projects/${projectId}/chatbot/stream`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      session_id: sessionId,
      conversation_id: options.conversationId || null,
      apply_guardrails: true,
      include_sources: true,
      deep_agent_enabled: options.deepAgentEnabled,
      reasoning_enabled: options.reasoningEnabled,
      reasoning_depth: options.reasoningDepth,
      chat_mode: options.chatMode,
      chat_role: options.chatRole,
      memory_enabled: options.memoryEnabled,
      direct_llm_fallback_enabled: options.directLlmFallbackEnabled === true,
    }),
  });
  if (response.status === 401) {
    if (authFailureHandler) authFailureHandler();
    throw new Error('Session expired');
  }
  return response;
};

export const listChatSessions = async (token, projectId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/chatbot/sessions`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getChatHistory = async (token, projectId, sessionId) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${projectId}/chatbot/${sessionId}/history`,
    headers: { Authorization: `Bearer ${token}` },
  });

// ---- Voice (EAP-proxied; the browser never talks to the voice gateway directly) ----

const _voiceBase = (projectId) => `${baseUrl}/projects/${encodeURIComponent(projectId)}/voice/session`;
const VOICE_REQUEST_TIMEOUT_MS = 20000;

/** Speech engines the voice gateway exposes (service key stays on the server). */
export const getVoiceSttProviders = async (token, projectId, signal) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/voice/stt-providers`,
    headers: { Authorization: `Bearer ${token}` },
    timeout: VOICE_REQUEST_TIMEOUT_MS,
    signal,
  });

export const createVoiceSession = async (
  token,
  projectId,
  { sessionId, conversationId, locale, memoryEnabled, sttProvider } = {},
  signal,
) =>
  await axios({
    method: `post`,
    url: _voiceBase(projectId),
    headers: { Authorization: `Bearer ${token}` },
    data: {
      session_id: sessionId || null,
      conversation_id: conversationId || null,
      locale: locale || null,
      ...(typeof memoryEnabled === "boolean" ? { memory_enabled: memoryEnabled } : {}),
      ...(sttProvider ? { stt_provider: sttProvider } : {}),
    },
    timeout: VOICE_REQUEST_TIMEOUT_MS,
    signal,
  });

export const sendVoiceOffer = async (token, projectId, voiceSessionId, joinToken, offer, signal) =>
  await axios({
    method: `post`,
    url: `${_voiceBase(projectId)}/${encodeURIComponent(voiceSessionId)}/offer`,
    headers: { Authorization: `Bearer ${token}`, "X-Session-Token": joinToken },
    data: offer,
    timeout: VOICE_REQUEST_TIMEOUT_MS,
    signal,
  });

export const getVoiceSessionStatus = async (token, projectId, voiceSessionId, joinToken, signal) =>
  await axios({
    method: `get`,
    url: `${_voiceBase(projectId)}/${encodeURIComponent(voiceSessionId)}/status`,
    headers: { Authorization: `Bearer ${token}`, "X-Session-Token": joinToken },
    timeout: VOICE_REQUEST_TIMEOUT_MS,
    signal,
  });

export const setVoiceMicrophone = async (token, projectId, voiceSessionId, joinToken, muted, signal) =>
  await axios({
    method: `post`,
    url: `${_voiceBase(projectId)}/${encodeURIComponent(voiceSessionId)}/microphone`,
    headers: { Authorization: `Bearer ${token}`, "X-Session-Token": joinToken },
    data: { muted: Boolean(muted) },
    timeout: VOICE_REQUEST_TIMEOUT_MS,
    signal,
  });

/** Best-effort end; uses fetch keepalive so it can complete during page unload. */
export const endVoiceSession = (token, projectId, voiceSessionId, joinToken) =>
  fetch(`${_voiceBase(projectId)}/${encodeURIComponent(voiceSessionId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, "X-Session-Token": joinToken },
    keepalive: true,
  }).catch(() => undefined);

export const deleteChatSession = async (token, projectId, sessionId) =>
  await axios({
    method: `delete`,
    url: `${baseUrl}/projects/${projectId}/chatbot/${sessionId}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteChatMessageTurn = async (token, projectId, conversationId, turnIndex) =>
  await axios({
    method: `delete`,
    url: `${baseUrl}/projects/${projectId}/chatbot/${conversationId}/messages/${turnIndex}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getPendingCollections = async (token) =>
  await axios({
    method: `get`,
    url: `${baseUrl}/collections/pending`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getCollectionDetails = async (token, collectionName, type = "deleted") =>
  await axios({
    method: `get`,
    url: `${baseUrl}/collections/pending/${collectionName}?type=${type}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const confirmDeleteCollection = async (token, collectionName, type = "deleted") =>
  await axios({
    method: "post",
    url:
      baseUrl +
      "/collections/pending/" +
      encodeURIComponent(collectionName) +
      "?type=" +
      encodeURIComponent(type),
    headers: { Authorization: `Bearer ${token}` },
  });

export const restoreProject = async (token, collectionName, type = "deleted") =>
  await axios({
    method: "post",
    url:
      baseUrl +
      "/collections/pending/" +
      encodeURIComponent(collectionName) +
      "/restore?type=" +
      encodeURIComponent(type),
    headers: { Authorization: `Bearer ${token}` },
  });

export const checkCollectionExists = async (collectionName, vectorStoreType = "milvus", token) =>
  await axios({
    method: "get",
    url:
      baseUrl +
      "/collections/check-collection/" +
      encodeURIComponent(collectionName) +
      "?vector_store_type=" +
      encodeURIComponent(vectorStoreType),
    headers: { Authorization: `Bearer ${token}` },
  });

// ----- RBAC: Auth (login/register already above), Users, Groups, Projects -----

export const listUsers = async (token) =>
  await axios({
    method: "get",
    // Trailing slash required: FastAPI @router.get("/") under /users redirects
    // /api/users -> /users/ and nginx would drop the /api prefix on follow.
    url: `${baseUrl}/users/`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const listUnassignedUsers = async (token) =>
  await axios({
    method: "get",
    url: `${baseUrl}/users/unassigned`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const changeUserRole = async (token, userId, role) =>
  await axios({
    method: "patch",
    url: `${baseUrl}/users/${userId}/role`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { role },
  });

export const updateUserCategories = async (token, userId, categoryIds) =>
  await axios({
    method: "patch",
    url: `${baseUrl}/users/${userId}/categories`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { category_ids: categoryIds || [] },
  });

export const listRoles = async (token) =>
  await axios({
    method: "get",
    url: `${baseUrl}/roles`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const listCategories = listRoles;

export const createRole = async (token, name, description = "") =>
  await axios({
    method: "post",
    url: `${baseUrl}/roles`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { name, description: description || null },
  });

export const createCategory = createRole;

export const updateRole = async (token, roleId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/roles/${encodeURIComponent(roleId)}`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const updateCategory = updateRole;

export const deleteRole = async (token, roleId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/roles/${encodeURIComponent(roleId)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteCategory = deleteRole;

export const listGroups = async (token) =>
  await axios({
    method: "get",
    url: `${baseUrl}/rbac/groups`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const createGroup = async (token, name, description = "") =>
  await axios({
    method: "post",
    url: `${baseUrl}/rbac/groups`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { name, description },
  });

export const updateGroup = async (token, groupId, name, description) =>
  await axios({
    method: "put",
    url: `${baseUrl}/rbac/groups/${groupId}`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { name, description },
  });

export const deleteGroup = async (token, groupId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/rbac/groups/${groupId}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const assignAdminToGroup = async (token, groupId, adminUserId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/rbac/groups/${groupId}/admins/${adminUserId}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const removeAdminFromGroup = async (token, groupId, adminUserId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/rbac/groups/${groupId}/admins/${adminUserId}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const listRbacProjects = async (token, groupId = null, options = {}) => {
  const params = new URLSearchParams();
  if (groupId) params.set("group_id", groupId);
  const q = params.toString();
  const url = q ? `${baseUrl}/rbac/projects?${q}` : `${baseUrl}/rbac/projects`;
  const forceRefresh = Boolean(options.forceRefresh);
  const signal = options.signal;
  const cacheKey = `${token || ""}::${groupId || ""}`;
  const now = Date.now();
  const cached = _rbacProjectsCache.get(cacheKey);
  if (!forceRefresh && cached && now - cached.ts < RBAC_PROJECTS_CACHE_TTL_MS) {
    return cached.response;
  }
  if (!forceRefresh && !signal && _rbacProjectsInflight.has(cacheKey)) {
    return _rbacProjectsInflight.get(cacheKey);
  }
  const req = axios({
    method: "get",
    url,
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
    .then((response) => {
      _rbacProjectsCache.set(cacheKey, { ts: Date.now(), response });
      return response;
    })
    .finally(() => {
      _rbacProjectsInflight.delete(cacheKey);
    });
  if (!forceRefresh && !signal) {
    _rbacProjectsInflight.set(cacheKey, req);
  }
  return req;
};

export const getRbacProjectByCoreId = async (token, coreProjectId) =>
  await axios({
    method: "get",
    url: `${baseUrl}/rbac/projects/by-core/${encodeURIComponent(coreProjectId)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const createRbacProject = async (token, name, description, groupId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/rbac/projects`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { name, description, group_id: groupId },
  });

export const createRbacProjectWithConfig = async (token, name, description, groupId, config) =>
  await axios({
    method: "post",
    url: `${baseUrl}/rbac/projects`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { name, description, group_id: groupId, config },
  });

export const updateRbacProject = async (token, projectId, name, description) =>
  await axios({
    method: "put",
    url: `${baseUrl}/rbac/projects/${projectId}`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { name, description },
  });

export const deleteRbacProject = async (token, projectId, emptyS3 = false) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/rbac/projects/${projectId}${emptyS3 ? '?empty_s3=true' : ''}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const addUserToRbacProject = async (token, projectId, userId, roleIds = []) =>
  await axios({
    method: "post",
    url: `${baseUrl}/rbac/projects/${projectId}/users/${userId}`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { role_ids: roleIds || [], category_ids: roleIds || [] },
  });

export const removeUserFromRbacProject = async (token, projectId, userId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/rbac/projects/${projectId}/users/${userId}`,
    headers: { Authorization: `Bearer ${token}` },
  });


// ---------------------------------------------------------------------------
// File pipeline (per project)
// ---------------------------------------------------------------------------

export const runFilePipelineNow = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/run`,
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });

export const ingestGraphRagFiles = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/graphrag/ingest`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const removeGraphRagFiles = async (token, projectId, payload = {}) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/graphrag/remove`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const getFilePipelineSchedule = async (token, projectId) =>
  await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/schedule`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateFilePipelineSchedule = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/schedule`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const listFilePipelineFiles = async (token, projectId, { syncS3 = false } = {}) =>
  await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files`,
    headers: { Authorization: `Bearer ${token}` },
    params: syncS3 ? { sync_s3: true } : undefined,
  });

export const getGithubFilePreview = async (token, projectId, { file_key, doc_id } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/github-preview${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getConfluenceFilePreview = async (token, projectId, { file_key, doc_id } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/confluence-preview${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getS3FilePreview = async (token, projectId, { file_key, doc_id } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/s3-preview${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getJiraFilePreview = async (token, projectId, { file_key, doc_id } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/jira-preview${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getAzureWikiFilePreview = async (token, projectId, { file_key, doc_id } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/azure-wiki-preview${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getAzureWikiFilePreviewPdf = async (token, projectId, { file_key, doc_id } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/azure-wiki-preview.pdf${q}`,
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
};

export const getAzureWikiImage = async (token, projectId, { file_key, doc_id, path } = {}) => {
  const params = new URLSearchParams();
  if (file_key) params.set("file_key", file_key);
  if (doc_id) params.set("doc_id", doc_id);
  if (path) params.set("path", path);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/azure-wiki-image${q}`,
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
};

export const deleteFilePipelineFileRecord = async (token, projectId, fileId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/${encodeURIComponent(fileId)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const retryFilePipelineFile = async (token, projectId, fileId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/${encodeURIComponent(fileId)}/retry`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const cancelFilePipelineFile = async (token, projectId, fileId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/${encodeURIComponent(fileId)}/cancel`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const reprocessFilePipelineFile = async (token, projectId, fileId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/${encodeURIComponent(fileId)}/reprocess`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const removeKnowledgeFileFromIndex = async (token, projectId, fileId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/files/${encodeURIComponent(fileId)}/remove-from-index`,
    headers: { Authorization: `Bearer ${token}` },
  });

/** @deprecated use removeKnowledgeFileFromIndex */
export const removeConfluenceFileFromIndex = removeKnowledgeFileFromIndex;

export const getFilePipelineS3TriggerStatus = async (token, projectId) =>
  await axios({
    method: "get",
    url: `${baseUrl}/projects/${encodeURIComponent(projectId)}/file-pipeline/s3-trigger-status`,
    headers: { Authorization: `Bearer ${token}` },
  });

// ---------------------------------------------------------------------------
// Configuration / model catalog
// ---------------------------------------------------------------------------

export const getModelCatalog = async (options = {}) =>
  await axios({
    method: "get",
    url: `${baseUrl}/config/models`,
    signal: options.signal,
  });

// ---------------------------------------------------------------------------
// S3 file management
// ---------------------------------------------------------------------------

const _s3BucketQuery = (bucket) =>
  bucket ? `bucket=${encodeURIComponent(bucket)}` : "";

const _fileContentType = (file) =>
  (file && typeof file.type === "string" && file.type.trim()) || "application/octet-stream";

const _presignNotAvailable = (error) => {
  const status = error?.response?.status;
  return status === 404 || status === 405 || status === 501;
};

const _buildS3CorsError = (uploadUrl) => {
  const err = new Error("S3 CORS configuration is missing for browser uploads.");
  err.response = {
    data: {
      detail:
        "S3 bucket CORS is blocking browser upload. Allow origin https://enterpriseaihub.thbscoetg.com with methods PUT, POST, GET, HEAD and headers *.",
    },
  };
  err.uploadUrl = uploadUrl;
  return err;
};

const _s3UploadViaPresign = async (token, file, bucket = null, key = null, categoryId = null) => {
  const body = {
    filename: file?.name || "upload.bin",
    content_type: _fileContentType(file),
  };
  if (bucket) body.bucket = bucket;
  if (key) body.key = key;
  if (categoryId) body.role_id = categoryId;

  const presignResp = await axios({
    method: "post",
    url: `${baseUrl}/s3/upload/presign`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: body,
  });
  const payload = presignResp?.data || {};
  const uploadUrl = payload.upload_url;
  if (!uploadUrl) {
    throw new Error("Missing upload_url from presign response");
  }

  try {
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": _fileContentType(file) },
    });
  } catch (error) {
    const isNetworkOrCors = !error?.response;
    const isS3Url = String(uploadUrl).includes(".amazonaws.com/");
    if (isNetworkOrCors && isS3Url) {
      throw _buildS3CorsError(uploadUrl);
    }
    throw error;
  }

  return {
    data: {
      key: payload.key || key || file?.name,
      bucket: payload.bucket || bucket || null,
      url: payload.url || uploadUrl,
      direct_upload: true,
    },
  };
};

export const listProjectS3Buckets = async (token) =>
  await axios({
    method: "get",
    url: `${baseUrl}/s3/project-buckets`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const listS3Files = async (token, prefix = null, bucket = null) => {
  const params = new URLSearchParams();
  if (prefix) params.set("prefix", prefix);
  if (bucket) params.set("bucket", bucket);
  const q = params.toString();
  return await axios({
    method: "get",
    url: q ? `${baseUrl}/s3/files?${q}` : `${baseUrl}/s3/files`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const listProjectS3Files = async (token, projectId, options = {}) => {
  const params = new URLSearchParams({ project_id: projectId });
  for (const [k, v] of Object.entries(options)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  return await axios({
    method: "get",
    url: `${baseUrl}/s3/project-files?${params.toString()}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const checkS3BucketPermissions = async (token, bucket = null) => {
  const q = bucket ? `?${_s3BucketQuery(bucket)}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/s3/permissions${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const uploadS3File = async (token, file, bucket = null, categoryId = null) => {
  try {
    return await _s3UploadViaPresign(token, file, bucket, null, categoryId);
  } catch (error) {
    if (!_presignNotAvailable(error)) {
      throw error;
    }
  }
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  if (bucket) params.set("bucket", bucket);
  if (categoryId) params.set("role_id", categoryId);
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "post",
    url: `${baseUrl}/s3/upload${q}`,
    headers: { Authorization: `Bearer ${token}` },
    data: formData,
  });
};

export const reuploadS3File = async (token, key, file, bucket = null) => {
  try {
    return await _s3UploadViaPresign(token, file, bucket, key);
  } catch (error) {
    if (!_presignNotAvailable(error)) {
      throw error;
    }
  }
  const formData = new FormData();
  formData.append("file", file);
  const q = bucket ? `?${_s3BucketQuery(bucket)}` : "";
  return await axios({
    method: "put",
    url: `${baseUrl}/s3/${encodeURIComponent(key)}${q}`,
    headers: { Authorization: `Bearer ${token}` },
    data: formData,
  });
};

export const deleteS3File = async (token, key, bucket = null) => {
  const q = bucket ? `?${_s3BucketQuery(bucket)}` : "";
  return await axios({
    method: "delete",
    url: `${baseUrl}/s3/${encodeURIComponent(key)}${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getS3DownloadUrl = async (token, key, bucket = null, forceDownload = false) => {
  const params = new URLSearchParams();
  if (bucket) params.set("bucket", bucket);
  if (forceDownload) params.set("download", "true");
  const q = params.toString() ? `?${params.toString()}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/s3/${encodeURIComponent(key)}/download${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getS3PreviewUrl = async (token, key, bucket = null) => {
  const q = bucket ? `?${_s3BucketQuery(bucket)}` : "";
  return await axios({
    method: "get",
    url: `${baseUrl}/s3/${encodeURIComponent(key)}/preview-link${q}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

// ---------------------------------------------------------------------------
// Admin: cost monitoring & budgets (super_admin)
// ---------------------------------------------------------------------------

export const getAdminCostSummary = async (token, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/summary?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminMyGroupsBudgetPool = async (token, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/my-groups/budget-pool?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminCostProjectDetail = async (token, projectId, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateAdminProjectBudget = async (token, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/budget`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const getAdminProjectBudget = async (token, projectId, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/budget?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminBudgetAlerts = async (token, limit = 100) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/budget-alerts?limit=${encodeURIComponent(limit)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

// Super admin: group (department) budgets
export const getAdminGroupsBudgetSummary = async (token, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/groups/budgets/summary?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminGroupBudgetDetail = async (token, groupId, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/groups/${encodeURIComponent(groupId)}/budget?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateAdminGroupBudget = async (token, groupId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/admin/cost/groups/${encodeURIComponent(groupId)}/budget`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const getAdminGroupProjectAllocation = async (token, groupId, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/groups/${encodeURIComponent(groupId)}/project-allocation-summary?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const updateAdminGroupProjectBudget = async (token, groupId, projectId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/admin/cost/groups/${encodeURIComponent(groupId)}/projects/${encodeURIComponent(projectId)}/budget`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

// ---------------------------------------------------------------------------
// Admin: per-user budgets (within a project)
// ---------------------------------------------------------------------------

export const getAdminProjectUserBudgets = async (token, projectId) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/user-budgets`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminProjectAllocationSummary = async (token, projectId) =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/allocation-summary`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const upsertAdminUserBudget = async (token, projectId, userId, payload) =>
  await axios({
    method: "put",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/budget`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const enableAdminUserBudget = async (token, projectId, userId) =>
  await axios({
    method: "patch",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/budget/enable`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const disableAdminUserBudget = async (token, projectId, userId) =>
  await axios({
    method: "patch",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/budget/disable`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const deleteAdminUserBudget = async (token, projectId, userId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/budget`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminUserUsage = async (token, projectId, userId, period = "monthly") =>
  await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/usage?period=${encodeURIComponent(period)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

// ---------------------------------------------------------------------------
// Budget extension requests
// ---------------------------------------------------------------------------

export const createBudgetExtensionRequest = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/budget-requests`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const listMyBudgetRequests = async (token, projectId = null, limit = 200) => {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (projectId) params.set("project_id", projectId);
  return await axios({
    method: "get",
    url: `${baseUrl}/budget-requests/my-requests?${params.toString()}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const cancelMyBudgetRequest = async (token, requestId) =>
  await axios({
    method: "delete",
    url: `${baseUrl}/budget-requests/${encodeURIComponent(requestId)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const listPendingBudgetRequests = async (token, limit = 200) =>
  await axios({
    method: "get",
    url: `${baseUrl}/budget-requests/pending?limit=${encodeURIComponent(limit)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const approveBudgetRequest = async (token, requestId, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/budget-requests/${encodeURIComponent(requestId)}/approve`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const rejectBudgetRequest = async (token, requestId, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/budget-requests/${encodeURIComponent(requestId)}/reject`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

// ---------------------------------------------------------------------------
// User: my usage / budgets
// ---------------------------------------------------------------------------

export const getMyUsage = async (token, period = "monthly") =>
  await axios({
    method: "get",
    url: `${baseUrl}/me/usage?period=${encodeURIComponent(period)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getMyUsageProject = async (token, projectId, period = "monthly") =>
  await axios({
    method: "get",
    url: `${baseUrl}/me/usage/${encodeURIComponent(projectId)}?period=${encodeURIComponent(period)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getMyUsageProjectDaily = async (token, projectId, days = 30) =>
  await axios({
    method: "get",
    url: `${baseUrl}/me/usage/${encodeURIComponent(projectId)}/daily?days=${encodeURIComponent(days)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getMyBudget = async (token, projectId, period = "monthly") =>
  await axios({
    method: "get",
    url: `${baseUrl}/me/budget/${encodeURIComponent(projectId)}?period=${encodeURIComponent(period)}`,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getAdminGuardrailAudit = async (token, projectId = null, limit = 100) => {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (projectId) params.set("project_id", projectId);
  return await axios({
    method: "get",
    url: `${baseUrl}/admin/cost/guardrail-audit?${params.toString()}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const clearAdminGuardrailAudit = async (token, payload) =>
  await axios({
    method: "post",
    url: `${baseUrl}/admin/cost/guardrail-audit/clear`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: payload,
  });

export const downloadChatStarterKit = async (token) =>
  await axios({
    method: "post",
    url: `${baseUrl}/starter-kit/download`,
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });

export const createStarterKitEntitlement = async (token, projectId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/starter-kit/entitlements`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { project_id: projectId },
  });

export const regenerateStarterKitEntitlement = async (token, projectId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/starter-kit/entitlements/regenerate`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    data: { project_id: projectId },
  });

export const listStarterKitEntitlements = async (token, options = {}) => {
  const forceRefresh = Boolean(options.forceRefresh);
  const signal = options.signal;
  const cacheKey = String(token || "");
  const cached = _starterEntitlementsCache.get(cacheKey);
  if (!forceRefresh && cached && Date.now() - cached.ts < STARTER_ENTITLEMENTS_CACHE_TTL_MS) {
    return cached.response;
  }
  if (!forceRefresh && !signal && _starterEntitlementsInflight.has(cacheKey)) {
    return _starterEntitlementsInflight.get(cacheKey);
  }
  const req = axios({
    method: "get",
    url: `${baseUrl}/starter-kit/entitlements`,
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
    .then((response) => {
      _starterEntitlementsCache.set(cacheKey, { ts: Date.now(), response });
      return response;
    })
    .finally(() => {
      _starterEntitlementsInflight.delete(cacheKey);
    });
  if (!forceRefresh && !signal) {
    _starterEntitlementsInflight.set(cacheKey, req);
  }
  return req;
};

export const downloadStarterKitEntitlement = async (token, entitlementId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/starter-kit/entitlements/${encodeURIComponent(entitlementId)}/download`,
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });

export const revokeStarterKitEntitlement = async (token, entitlementId) =>
  await axios({
    method: "post",
    url: `${baseUrl}/starter-kit/entitlements/${encodeURIComponent(entitlementId)}/revoke`,
    headers: { Authorization: `Bearer ${token}` },
  });


